"use client";

import { ArrowLeft, RefreshCw } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

export default function InteractiveNotFound() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [highScore, setHighScore] = useState(0);

    // Game State Refs (to avoid re-renders during loop)
    const snakeRef = useRef([{ x: 10, y: 10 }]);
    const foodRef = useRef({ x: 15, y: 15 });
    const dirRef = useRef({ x: 1, y: 0 });
    const nextDirRef = useRef({ x: 1, y: 0 }); // Buffer for next direction to prevent 180 turns
    const speedRef = useRef(150);
    const lastTimeRef = useRef(0);
    const gameLoopRef = useRef<number>(0);

    const GRID_SIZE = 20;
    const TILE_COUNT = 20; // 20x20 grid = 400x400 canvas

    // Initialize/Reset Game
    const resetGame = () => {
        snakeRef.current = [{ x: 10, y: 10 }];
        foodRef.current = { x: 15, y: 15 };
        dirRef.current = { x: 1, y: 0 };
        nextDirRef.current = { x: 1, y: 0 };
        setScore(0);
        setGameOver(false);
        setGameStarted(true);
        speedRef.current = 150;
    };

    // Keyboard Controls
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
                e.preventDefault();
            }

            if (!gameStarted && (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight")) {
                resetGame();
                return;
            }

            switch (e.key) {
                case "ArrowUp":
                    if (dirRef.current.y === 0) nextDirRef.current = { x: 0, y: -1 };
                    break;
                case "ArrowDown":
                    if (dirRef.current.y === 0) nextDirRef.current = { x: 0, y: 1 };
                    break;
                case "ArrowLeft":
                    if (dirRef.current.x === 0) nextDirRef.current = { x: -1, y: 0 };
                    break;
                case "ArrowRight":
                    if (dirRef.current.x === 0) nextDirRef.current = { x: 1, y: 0 };
                    break;
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [gameStarted]);

    // Force Dark Mode & Layout Fixes
    useEffect(() => {
        const wrapper = document.getElementById("main-layout-wrapper");
        const footer = document.querySelector("footer");
        const navbar = document.querySelector("header"); // If we want to adjust navbar too
        const main = document.querySelector("main");

        const originalWrapperBg = wrapper?.style.backgroundColor;
        const originalFooterFilter = footer?.style.filter;
        const originalMainPaddingTop = main?.style.paddingTop;

        if (wrapper) {
            wrapper.style.backgroundColor = "#000000";
            // Also force it to cover the top padding if needed, but background change should be enough
            // if the body is black.
            document.body.style.backgroundColor = "#000000";
        }

        if (main) {
            main.style.paddingTop = "0px";
        }

        if (footer) {
            footer.style.filter = "invert(1) hue-rotate(180deg)";
            footer.style.transition = "filter 0.5s ease";
        }

        return () => {
            if (wrapper) wrapper.style.backgroundColor = originalWrapperBg || "";
            document.body.style.backgroundColor = ""; // Revert to default
            if (main) main.style.paddingTop = originalMainPaddingTop || "";
            if (footer) footer.style.filter = originalFooterFilter || "";
        };
    }, []);

    // Game Loop
    useEffect(() => {
        if (!gameStarted || gameOver) return;

        const loop = (time: number) => {
            gameLoopRef.current = requestAnimationFrame(loop);

            if (time - lastTimeRef.current < speedRef.current) return;
            lastTimeRef.current = time;

            update();
            draw();
        };

        gameLoopRef.current = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(gameLoopRef.current);
    }, [gameStarted, gameOver]);

    const update = () => {
        // Update Direction
        dirRef.current = nextDirRef.current;

        const head = { ...snakeRef.current[0] };
        head.x += dirRef.current.x;
        head.y += dirRef.current.y;

        // Wall Collision
        if (head.x < 0 || head.x >= TILE_COUNT || head.y < 0 || head.y >= TILE_COUNT) {
            handleGameOver();
            return;
        }

        // Self Collision
        for (const segment of snakeRef.current) {
            if (head.x === segment.x && head.y === segment.y) {
                handleGameOver();
                return;
            }
        }

        snakeRef.current.unshift(head);

        // Food Collision
        if (head.x === foodRef.current.x && head.y === foodRef.current.y) {
            setScore(s => s + 10);
            speedRef.current = Math.max(50, speedRef.current - 2); // Increase speed
            spawnFood();
        } else {
            snakeRef.current.pop();
        }
    };

    const spawnFood = () => {
        let newFood: { x: number; y: number };
        while (true) {
            newFood = {
                x: Math.floor(Math.random() * TILE_COUNT),
                y: Math.floor(Math.random() * TILE_COUNT)
            };
            // Check if food spawns on snake
            const onSnake = snakeRef.current.some(s => s.x === newFood.x && s.y === newFood.y);
            if (!onSnake) break;
        }
        foodRef.current = newFood;
    };

    const handleGameOver = () => {
        setGameOver(true);
        if (score > highScore) setHighScore(score);
    };

    const draw = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Clear
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Grid (Subtle)
        ctx.fillStyle = "rgba(255, 255, 255, 0.03)";
        for (let x = 0; x < TILE_COUNT; x++) {
            for (let y = 0; y < TILE_COUNT; y++) {
                if ((x + y) % 2 === 0) {
                    ctx.fillRect(x * GRID_SIZE, y * GRID_SIZE, GRID_SIZE, GRID_SIZE);
                }
            }
        }

        // Snake
        ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
        ctx.shadowBlur = 10;
        ctx.shadowColor = "rgba(255, 255, 255, 0.5)";
        snakeRef.current.forEach((segment, i) => {
            // Head is slightly larger or different?
            const isHead = i === 0;
            ctx.beginPath();
            ctx.roundRect(
                segment.x * GRID_SIZE + 1,
                segment.y * GRID_SIZE + 1,
                GRID_SIZE - 2,
                GRID_SIZE - 2,
                4
            );
            ctx.fill();
        });
        ctx.shadowBlur = 0;

        // Food
        ctx.fillStyle = "#fafafa";
        ctx.shadowBlur = 15;
        ctx.shadowColor = "#fafafa";
        ctx.beginPath();
        ctx.arc(
            foodRef.current.x * GRID_SIZE + GRID_SIZE / 2,
            foodRef.current.y * GRID_SIZE + GRID_SIZE / 2,
            GRID_SIZE / 3,
            0,
            Math.PI * 2
        );
        ctx.fill();
        ctx.shadowBlur = 0;
    };

    // Initial Draw
    useEffect(() => {
        if (!gameStarted) {
            draw();
        }
    }, [gameStarted]);

    return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden font-sans">
            {/* Background Ambience */}
            <div className="absolute inset-0 z-0">
                <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-purple-900/20 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-blue-900/20 rounded-full blur-[120px]" />
            </div>

            {/* Glass Container */}
            <div className="relative z-10 w-full max-w-4xl bg-white/5 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row">

                {/* Left Panel: 404 & Info */}
                <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col justify-center border-b md:border-b-0 md:border-r border-white/10">
                    <h1 className="text-8xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-white to-white/40 mb-2">
                        404
                    </h1>
                    <h2 className="text-2xl text-white/90 font-medium mb-6">Page Not Found</h2>
                    <p className="text-white/60 mb-8 leading-relaxed">
                        The page you are looking for has been moved, deleted, or possibly never existed.
                        <br /><br />
                        While you are here, why not set a new high score?
                    </p>

                    <div className="flex flex-col gap-4">
                        <Link
                            href="/"
                            className="inline-flex items-center justify-center px-6 py-3 bg-white text-black font-semibold rounded-full hover:bg-white/90 transition-all hover:scale-105 active:scale-95 w-fit"
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Return Home
                        </Link>
                        {gameStarted && (
                            <div className="flex items-center gap-4 text-white/50 text-sm mt-4">
                                <span>Score: <span className="text-white font-mono">{score}</span></span>
                                <span>High Score: <span className="text-white font-mono">{highScore}</span></span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Panel: Game Area */}
                <div className="w-full md:w-1/2 bg-black/20 p-8 flex items-center justify-center relative">
                    <div className="relative">
                        <canvas
                            ref={canvasRef}
                            width={400}
                            height={400}
                            className="bg-black/40 rounded-xl shadow-inner border border-white/5 max-w-full h-auto"
                        />

                        {/* Overlay: Start / Game Over */}
                        {(!gameStarted || gameOver) && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-xl">
                                <div className="text-center p-6">
                                    {gameOver ? (
                                        <>
                                            <h3 className="text-2xl text-red-400 font-bold mb-2">Game Over</h3>
                                            <p className="text-white/70 mb-6 font-mono">Score: {score}</p>
                                        </>
                                    ) : (
                                        <h3 className="text-white font-medium mb-6">Ready to play?</h3>
                                    )}

                                    <button
                                        onClick={resetGame}
                                        className="inline-flex items-center px-6 py-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full transition-all hover:scale-105 active:scale-95 backdrop-blur-md"
                                    >
                                        {gameOver ? <RefreshCw className="mr-2 h-4 w-4" /> : null}
                                        {gameOver ? "Try Again" : "Start Game"}
                                    </button>
                                    {!gameStarted && !gameOver && (
                                        <p className="text-white/30 text-xs mt-4">Use Arrow Keys to Move</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
